import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, SwitchCamera, X } from 'lucide-react';

interface CameraCaptureModalProps {
  show: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ show, onClose, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [capturing, setCapturing] = useState(false);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!show) {
      stopStream();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    const start = async () => {
      stopStream();
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('دوربین در این مرورگر پشتیبانی نمی‌شود. از آپلود فایل استفاده کنید.');
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setLoading(false);
          setError(
            e instanceof Error
              ? e.message || 'دسترسی به دوربین ممکن نشد.'
              : 'دسترسی به دوربین ممکن نشد.'
          );
        }
      }
    };

    void start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [show, facingMode]);

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!show) return null;

  const takePhoto = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setCapturing(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('خطا در ثبت تصویر');
      ctx.drawImage(video, 0, 0);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.88)
      );
      if (!blob) throw new Error('خطا در ذخیره تصویر');
      const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopStream();
      onCapture(file);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در ثبت تصویر');
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-4"
      dir="rtl"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-bold text-gray-800">گرفتن فیش با دوربین</h3>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="relative bg-black aspect-[4/3]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <Loader2 className="animate-spin" size={28} />
            </div>
          )}
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-sm text-white/90">
              <p>{error}</p>
              <p className="text-xs text-white/60">می‌توانید از دکمه «آپلود فایل» استفاده کنید.</p>
            </div>
          ) : (
            <video
              ref={videoRef}
              playsInline
              muted
              className="h-full w-full object-cover"
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-3">
          <button
            type="button"
            onClick={() => setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'))}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100"
            title="تعویض دوربین"
          >
            <SwitchCamera size={18} />
            تعویض
          </button>
          <button
            type="button"
            disabled={!!error || loading || capturing}
            onClick={() => void takePhoto()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
          >
            {capturing ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            گرفتن عکس
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
          >
            انصراف
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraCaptureModal;
